import React from 'react';
import { Box, Stack, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import PlaceAddressMapPreview from './PlaceAddressMapPreview';
import type { PlaceAddressDraft } from '../../utils/placeAddress';

export interface PlaceAddressReadOnlyFieldsProps {
  value: PlaceAddressDraft;
  /** Shown in the place-location field when `value.location` is empty. */
  placeLocationFallback?: string;
  compact?: boolean;
}

function resolvePlaceLocationLabel(value: PlaceAddressDraft, fallback?: string): string {
  const fromLocation = value.location?.trim();
  if (fromLocation) {
    return fromLocation;
  }
  const fromParts = [value.addressLine1, value.city].filter((part) => part?.trim()).join(', ');
  if (fromParts) {
    return fromParts;
  }
  return fallback?.trim() ?? '';
}

const PlaceAddressReadOnlyFields: React.FC<PlaceAddressReadOnlyFieldsProps> = ({
  value,
  placeLocationFallback,
  compact = false,
}) => {
  const { t } = useTranslation();
  const placeLocation = resolvePlaceLocationLabel(value, placeLocationFallback);

  return (
    <Stack spacing={compact ? 1 : 1.5}>
      <TextField
        label={t('space.placeLocation', 'Find in Google Maps')}
        value={placeLocation}
        fullWidth
        size={compact ? 'small' : 'medium'}
        disabled
      />

      <PlaceAddressMapPreview
        latitude={value.latitude}
        longitude={value.longitude}
        googleMapsUrl={value.googleMapsUrl}
        compact={compact}
      />

      <Box
        sx={{
          display: 'grid',
          gap: compact ? 1 : 1.5,
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' },
        }}
      >
        <TextField label={t('space.city', 'City')} value={value.city} fullWidth size={compact ? 'small' : 'medium'} disabled />
        <TextField label={t('space.state', 'State')} value={value.state} fullWidth size={compact ? 'small' : 'medium'} disabled />
        <TextField label={t('space.zipCode', 'Zip Code')} value={value.zipCode} fullWidth size={compact ? 'small' : 'medium'} disabled />
      </Box>

      <Box
        sx={{
          display: 'grid',
          gap: compact ? 1 : 1.5,
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
        }}
      >
        <TextField
          label={t('space.addressLine1', 'Address Line 1')}
          value={value.addressLine1}
          fullWidth
          size={compact ? 'small' : 'medium'}
          disabled
        />
        <TextField
          label={t('space.addressLine2', 'Address Line 2')}
          value={value.addressLine2}
          fullWidth
          size={compact ? 'small' : 'medium'}
          disabled
        />
      </Box>

      <TextField
        label={t('space.googleMapsURL', 'Google Maps URL')}
        value={value.googleMapsUrl}
        fullWidth
        size={compact ? 'small' : 'medium'}
        disabled
        type="url"
      />
    </Stack>
  );
};

export default PlaceAddressReadOnlyFields;
